"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { ArrowLeft, X } from "lucide-react";
import Link from "@/components/atlas-link";
import { Button } from "@/components/ui/button";
import { useRouter } from "@/lib/navigation";

/**
 * The selected note. From `lg` it sits beside the feed, which slides left
 * into a rail to make room; on phones it replaces the feed. The panel stays
 * mounted while the reader moves between notes, so only opening animates.
 */
export function NoteReaderPanel({
  children,
  closeHref,
  slug,
}: {
  children: ReactNode;
  closeHref: string;
  slug: string;
}) {
  const router = useRouter();
  const panelRef = useRef<HTMLElement | null>(null);

  // Start each note at the top instead of the previous note's scroll position.
  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [slug]);

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      // Leave Escape to dialogs and lightboxes opened from inside the note.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      router.push(closeHref, { scroll: false });
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [closeHref, router]);

  return (
    <section
      aria-label="Note reader"
      className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain lg:motion-safe:animate-in lg:motion-safe:fade-in lg:motion-safe:slide-in-from-right-6 lg:motion-safe:duration-300 lg:motion-safe:ease-out"
      ref={panelRef}
    >
      <div className="sticky top-0 z-10 flex h-12 items-center border-b border-border/60 bg-background/95 px-2 backdrop-blur-sm sm:px-4">
        <Button asChild className="lg:hidden" size="sm" variant="ghost">
          <Link href={closeHref} scroll={false}>
            <ArrowLeft />
            All notes
          </Link>
        </Button>
        <Button
          asChild
          className="relative hit-area ml-auto hidden lg:inline-flex"
          size="icon-sm"
          title="Close (Esc)"
          variant="ghost"
        >
          <Link aria-label="Close note" href={closeHref} scroll={false}>
            <X />
          </Link>
        </Button>
      </div>
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-10 lg:py-8 atlas-v2:min-w-0">
        {children}
      </div>
    </section>
  );
}
